import { Component, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DashboardClaim } from '@lsq-frontend/remote-services/enterprise/common';
import { Color, forLifeOf, LifecycleAware, Masks, Modal, Unmasks } from '@lsq-frontend/common-ui';
import {
    ClaimService,
    FeatureFlagService,
    Icons,
    WorkflowMonitorService
} from '@lsq-frontend/common-ui';
import { LsqValidators } from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { BehaviorSubject, Subscription } from 'rxjs';
import { first, map, skip } from 'rxjs/operators';
import { SetFeeStructureRequest } from '@lsq-frontend/remote-services/all-java/financing-mk1-services';
import { AdminManageSupplierSetFeeStructureRequest } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-supplier/set-fee-structure';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { FeeType, FeeTypeDisplayValue } from '../../../../models';
import { SaveFeeStructureModalComponent } from '../../../modals/save-fee-structure-modal.component';
import { ManageSupplierService } from '../../manage-supplier.service';

@LifecycleAware()
@Component({
    selector: 'supplier-implementation-fee-structure',
    styleUrls: ['./supplier-implementation-fee-structure.component.scss'],
    template: `
        <card [headerTitle]="'Fee Structure'" [style.marginBottom]="'3rem'">
            <div card-body class="implementation-card-body">
                <div class="buyer-settings">
                    <p class="label">Current Buyer Settings</p>
                    <div [style.marginRight]="'5rem'">
                        <p class="data-label buyer-fee-type-label">
                            {{ feeTypeDisplay[buyerSettingsFeeType$ | async] }}
                        </p>
                        <p class="header data-value-fee">
                            {{ buyerSettingsFeeValue$ | async }}
                        </p>
                    </div>
                    <div>
                        <p class="data-label">
                            Ineligible Days Before Terms
                        </p>
                        <p class="header data-value-terms">
                            {{ inelegibleDays$ | async }}
                        </p>
                    </div>
                </div>

                <lsq-label
                    class="override-label"
                    [style.marginBottom]="'2rem'"
                    label="Buyer Setting Overrides"
                    subLabel="Click the checkbox below for each field to change the default buyer setting displayed."
                ></lsq-label>

                <div class="fee-structure-controls">
                    <div class="control-row">
                        <lsq-checkbox
                            [disabled]="!(canEditSuppliers$ | async)"
                            [(ngModel)]="isOverridingFee"
                            (ngModelChange)="markPageUpdated()"
                            class="checkbox-fee"
                        ></lsq-checkbox>
                        <div [style.width]="'100%'">
                            <lsq-label
                                label="Fee Type"
                                subLabel="The fee rate program type to be used for the supplier, set to whole percentage up to four decimal places."
                            ></lsq-label>

                            <form class="control-inputs" [formGroup]="programTypeFormGroup">
                                <lsq-select
                                    [disableControl]="!isOverridingFee"
                                    name="feeType"
                                    [options]="feeTypeOptions"
                                    formControlName="feeType"
                                    [group]="programTypeFormGroup"
                                ></lsq-select>
                                <lsq-input
                                    [disableControl]="!isOverridingFee"
                                    name="feePercent"
                                    formControlName="feePercent"
                                    [group]="programTypeFormGroup"
                                    placeholder="Percent"
                                    [textFormatter]="
                                        programTypeFormGroup.value.feeType ===
                                        feeType.DAILY_DECAYING_FEE
                                            ? formatHbpsInput
                                            : formatBpsInput
                                    "
                                    [handleKeypress]="handleBpsInput"
                                    [mask]="
                                        programTypeFormGroup.value.feeType ===
                                        feeType.DAILY_DECAYING_FEE
                                            ? hbpsAsPercentMask
                                            : bpsAsPercentMask
                                    "
                                    [appendIcon]="[icons.FaPercent, color.GraphicGray]"
                                >
                                </lsq-input>
                            </form>
                        </div>
                    </div>
                    <div class="control-row">
                        <lsq-checkbox
                            [disabled]="!(canEditSuppliers$ | async)"
                            [(ngModel)]="isOverridingIneligibleDaysBeforeTerms"
                            (ngModelChange)="markPageUpdated()"
                            class="checkbox-terms"
                        ></lsq-checkbox>
                        <div [style.width]="'100%'">
                            <lsq-label
                                label="Ineligible Days Before Terms"
                                subLabel="Set the number of days before terms that the invoice is eligible for payment."
                            ></lsq-label>

                            <form [formGroup]="daysBeforeTermsFormGroup" [style.width]="'100%'">
                                <lsq-input
                                    [disableControl]="!isOverridingIneligibleDaysBeforeTerms"
                                    formControlName="daysBeforeTerms"
                                    name="daysBeforeTerms"
                                    [group]="daysBeforeTermsFormGroup"
                                    type="number"
                                    placeholder="Number of Days"
                                >
                                </lsq-input>
                            </form>
                        </div>
                    </div>
                </div>

                <lsq-button
                    [style.marginTop]="'2rem'"
                    (buttonClick)="saveFeeStructure()"
                    [disabled]="!(canEditSuppliers$ | async)"
                >
                    Save
                </lsq-button>
            </div>
        </card>
    `
})
export class ManageSupplierImplementationFeeStructureComponent implements OnDestroy {
    public supplierRelationship$ = this._manageSupplierService.currentSupplierRelationship$;
    public feeType = FeeType;
    public feeTypeDisplay = FeeTypeDisplayValue;

    public isOverridingFee = false;
    public feeTypeOptions = [
        {
            label: 'Daily Decaying Fee Rate',
            value: FeeType.DAILY_DECAYING_FEE
        }
    ];
    public programTypeFormGroup = new FormGroup({
        feeType: new FormControl(null, [Validators.required]),
        feePercent: new FormControl(null, [Validators.required])
    });

    public isOverridingIneligibleDaysBeforeTerms = false;
    public daysBeforeTermsFormGroup = new FormGroup({
        daysBeforeTerms: new FormControl(null, [
            Validators.required,
            LsqValidators.wholeNumber,
            LsqValidators.maxNumber(10000),
            LsqValidators.minNumber(0)
        ])
    });

    public readonly icons = Icons;
    public readonly color = Color;
    public readonly hbpsAsPercentMask = Masks.HbpsAsPercentMask;
    public readonly bpsAsPercentMask = Masks.BpsAsPercentMask;

    public buyerSettingsFeeType$ = this.supplierRelationship$.pipe(
        map((relationship) => {
            if (!relationship) {
                return FeeType.NOT_SET;
            }

            let feeStructure = FeeType.NOT_SET;
            if (!!relationship.buyerDecayingFeeDailyRatePercent) {
                this._manageSupplierService.defaultSettings.feeStructure = feeStructure =
                    FeeType.DAILY_DECAYING_FEE;
            } else if (!!relationship.buyerDefaultFlatFeeRatePercent) {
                this._manageSupplierService.defaultSettings.feeStructure = feeStructure =
                    FeeType.FLAT_FEE;
            } else if (!!relationship.platformDecayingFeeDailyRatePercent) {
                this._manageSupplierService.defaultSettings.feeStructure = feeStructure =
                    FeeType.DAILY_DECAYING_FEE;
            } else {
                this._manageSupplierService.defaultSettings.feeStructure = feeStructure;
            }

            if (relationship.relationshipDecayingFeeDailyRatePercent) {
                this._manageSupplierService.currentSettings.feeStructure = this._manageSupplierService.currentOverridingSettings.feeStructure =
                    FeeType.DAILY_DECAYING_FEE;
            } else if (relationship.relationshipFlatFeeRatePercent) {
                this._manageSupplierService.currentSettings.feeStructure = this._manageSupplierService.currentOverridingSettings.feeStructure =
                    FeeType.FLAT_FEE;
            } else {
                this._manageSupplierService.currentSettings.feeStructure = this._manageSupplierService.currentOverridingSettings.feeStructure = feeStructure;
            }

            return feeStructure;
        })
    );

    public buyerSettingsFeeValue$ = this.supplierRelationship$.pipe(
        map((relationship) => {
            if (!relationship) {
                return '—';
            }

            const buyerValue =
                relationship.buyerDecayingFeeDailyRatePercent ??
                relationship.buyerDefaultFlatFeeRatePercent ??
                relationship.platformDecayingFeeDailyRatePercent;
            const relationshipValue =
                relationship.relationshipDecayingFeeDailyRatePercent ??
                relationship.relationshipFlatFeeRatePercent ??
                buyerValue;

            if (relationshipValue) {
                this._manageSupplierService.currentSettings.feeRate = this._manageSupplierService.currentOverridingSettings.feeRate = relationshipValue;
            }

            if (buyerValue) {
                this._manageSupplierService.defaultSettings.feeRate = buyerValue;
                return `${buyerValue}%`;
            }

            return '—';
        })
    );

    public inelegibleDays$ = this.supplierRelationship$.pipe(
        map((relationship) => {
            if (!relationship) {
                return '—';
            }

            const buyerValue =
                relationship.buyerIneligibleDaysBeforeTerms ??
                relationship.platformIneligibleDaysBeforeTerms ??
                relationship.defaultIneligibleDaysBeforeTerms;
            const relationshipValue =
                relationship.relationshipIneligibleDaysBeforeTerms ?? buyerValue;

            if (relationshipValue) {
                this._manageSupplierService.currentSettings.ineligibleDaysBeforeTerms = this._manageSupplierService.currentOverridingSettings.ineligibleDaysBeforeTerms = Number(
                    relationshipValue
                );
            }

            if (buyerValue) {
                this._manageSupplierService.defaultSettings.ineligibleDaysBeforeTerms = Number(
                    buyerValue
                );
                return buyerValue;
            }

            return '—';
        })
    );

    public canEditSuppliers$ = this._claimService.isOn(DashboardClaim.AdminEditSupplier);

    public programTypeFormGroupSub: Subscription;
    public daysBeforeTermsFormGroupSub: Subscription;

    constructor(
        private _manageSupplierService: ManageSupplierService,
        private _modalService: ModalService,
        private _claimService: ClaimService,
        private _featureFlagService: FeatureFlagService,
        private _workflowMonitorService: WorkflowMonitorService
    ) {
        this.supplierRelationship$.pipe(forLifeOf(this)).subscribe((relationship) => {
            if (this._featureFlagService.isOnSync(FeatureFlags.RelationshipLevelFlatFee)) {
                this.feeTypeOptions.push({
                    label: 'Flat Fee Rate',
                    value: FeeType.FLAT_FEE
                });
            }

            this.isOverridingFee =
                !!relationship?.relationshipDecayingFeeDailyRatePercent ||
                !!relationship?.relationshipFlatFeeRatePercent;
            this.isOverridingIneligibleDaysBeforeTerms =
                relationship?.relationshipIneligibleDaysBeforeTerms !== null;

            // If both fee types are present on a relationship, display the Decaying fee rate since
            // that takes precedence over flat fees.
            this.programTypeFormGroup.reset({
                feeType: !!relationship?.relationshipDecayingFeeDailyRatePercent
                    ? FeeType.DAILY_DECAYING_FEE
                    : !!relationship?.relationshipFlatFeeRatePercent
                    ? FeeType.FLAT_FEE
                    : FeeType.DAILY_DECAYING_FEE,
                feePercent: !!relationship?.relationshipDecayingFeeDailyRatePercent
                    ? this.formatHbpsInput(relationship?.relationshipDecayingFeeDailyRatePercent)
                    : !!relationship?.relationshipFlatFeeRatePercent
                    ? this.formatBpsInput(relationship?.relationshipFlatFeeRatePercent)
                    : null
            });
            this.daysBeforeTermsFormGroup.reset({
                daysBeforeTerms: this.isOverridingIneligibleDaysBeforeTerms
                    ? relationship?.relationshipIneligibleDaysBeforeTerms
                    : relationship?.buyerIneligibleDaysBeforeTerms ||
                      relationship?.platformIneligibleDaysBeforeTerms ||
                      relationship?.defaultIneligibleDaysBeforeTerms
            });
        });

        this.programTypeFormGroupSub = this.programTypeFormGroup.valueChanges
            .pipe(forLifeOf(this), skip(2))
            .subscribe((form) => {
                if (this.isOverridingFee) {
                    this._manageSupplierService.currentOverridingSettings.feeRate = Number(
                        form.feePercent
                    );
                    this._manageSupplierService.currentOverridingSettings.feeStructure =
                        form.feeType;
                } else {
                    this._manageSupplierService.currentOverridingSettings.feeRate = this._manageSupplierService.defaultSettings.feeRate;
                    this._manageSupplierService.currentOverridingSettings.feeStructure = this._manageSupplierService.defaultSettings.feeStructure;
                }
                this.markPageUpdated();
            });
        this.daysBeforeTermsFormGroupSub = this.daysBeforeTermsFormGroup.valueChanges
            .pipe(forLifeOf(this), skip(2))
            .subscribe((form) => {
                Number(
                    (this._manageSupplierService.currentOverridingSettings.ineligibleDaysBeforeTerms = Number(
                        form.daysBeforeTerms
                    ))
                );
                this.markPageUpdated();
            });
    }

    public ngOnDestroy(): void {
        this._workflowMonitorService.markWorkflowFinished();
        this.programTypeFormGroupSub.unsubscribe();
        this.daysBeforeTermsFormGroupSub.unsubscribe();
    }

    public markPageUpdated(): void {
        this._workflowMonitorService.markWorkflowStarted();
    }

    public async saveFeeStructure(): Promise<void> {
        this.programTypeFormGroup.updateValueAndValidity();
        this.programTypeFormGroup.markAllAsTouched();
        this.daysBeforeTermsFormGroup.updateValueAndValidity();
        this.daysBeforeTermsFormGroup.markAllAsTouched();

        if (!(this.daysBeforeTermsFormGroup.invalid || this.programTypeFormGroup.invalid)) {
            const isSubmitting$ = new BehaviorSubject<boolean>(false);
            const relationship = await this.supplierRelationship$.pipe(first()).toPromise();

            const newFeeType = this.isOverridingFee
                ? this.programTypeFormGroup.controls.feeType.value
                : await this.buyerSettingsFeeType$.pipe(first()).toPromise();

            const newFeePercent = this.isOverridingFee
                ? `${this.programTypeFormGroup.controls.feePercent.value}%`
                : await this.buyerSettingsFeeValue$.pipe(first()).toPromise();

            const newDaysBeforeTerms = this.isOverridingIneligibleDaysBeforeTerms
                ? this.daysBeforeTermsFormGroup.controls.daysBeforeTerms.value
                : relationship.buyerIneligibleDaysBeforeTerms ||
                  relationship.platformIneligibleDaysBeforeTerms ||
                  relationship.defaultIneligibleDaysBeforeTerms;

            this._modalService.showModal(
                new Modal({
                    bodyComponent: SaveFeeStructureModalComponent,
                    title: 'Save Fee Structure Settings',
                    showFooter: true,
                    showOkButton: true,
                    okButtonText: 'Save Settings',
                    showCancelButton: true,
                    cancelButtonText: 'Cancel',
                    data: {
                        supplierName: relationship.supplierPreferredName,
                        feeType: newFeeType,
                        feePercent: newFeePercent,
                        daysBeforeTerms: newDaysBeforeTerms
                    },
                    isSubmitting$,
                    onOkClick: async (): Promise<void> => {
                        isSubmitting$.next(true);

                        if (
                            this._featureFlagService.isOnSync(FeatureFlags.RelationshipLevelFlatFee)
                        ) {
                            await this._manageSupplierService.saveFeeStructure(
                                new AdminManageSupplierSetFeeStructureRequest({
                                    relationshipKey: relationship.relationshipKey,
                                    ineligibleDaysBeforeTerms: this
                                        .isOverridingIneligibleDaysBeforeTerms
                                        ? this.daysBeforeTermsFormGroup.controls.daysBeforeTerms
                                              .value
                                        : null,
                                    feeRatePercent: this.isOverridingFee
                                        ? this.programTypeFormGroup.controls.feePercent.value
                                        : null,
                                    feeType: this.isOverridingFee
                                        ? this.programTypeFormGroup.value.feeType
                                        : FeeType.NOT_SET
                                })
                            );
                        } else {
                            await this._manageSupplierService.saveFeeStructureLegacy(
                                new SetFeeStructureRequest({
                                    relationshipKey: relationship.relationshipKey,
                                    supplierKey: relationship.supplierKey,
                                    ineligibleDaysBeforeTerms: this
                                        .isOverridingIneligibleDaysBeforeTerms
                                        ? this.daysBeforeTermsFormGroup.controls.daysBeforeTerms
                                              .value
                                        : null,
                                    decayingFeeDailyRatePercent: this.isOverridingFee
                                        ? this.programTypeFormGroup.controls.feePercent.value
                                        : null
                                })
                            );
                        }

                        isSubmitting$.next(false);
                        this._modalService.closeModal();
                        this._workflowMonitorService.markWorkflowFinished();
                        await this._manageSupplierService.loadRelationship(
                            relationship.relationshipKey
                        );
                    }
                })
            );
        }
    }

    // TODO: Clean this up once inputs are rebuilt.
    public formatBpsInput(value: any): any {
        if (value === null || typeof value === 'undefined') {
            return value;
        }
        let formattedValue = value.toString().replace(Unmasks.CurrencyUnmask, '');

        if (value !== null && value !== '' && !isNaN(formattedValue)) {
            formattedValue = (+formattedValue).toFixed(2);
        }
        return formattedValue;
    }
    public formatHbpsInput(value: any): any {
        if (value === null || typeof value === 'undefined') {
            return value;
        }
        let formattedValue = value.toString().replace(Unmasks.CurrencyUnmask, '');

        if (value !== null && value !== '' && !isNaN(formattedValue)) {
            formattedValue = (+formattedValue).toFixed(4);
        }
        return formattedValue;
    }

    public handleBpsInput(event: KeyboardEvent): void {
        const pattern = /^-?[0-9\.-]/;
        const inputChar = String.fromCharCode(event.charCode);

        if (!pattern.test(inputChar)) {
            event.preventDefault();
        }
    }
}
